import { SaveOrganization } from 'model/organization';
import { validatePhone } from './phoneValidator';

/**
 * Validation rules for organization fields
 * Each rule returns an error message if validation fails, undefined if valid
 */
export const organizationValidationRules = {
    name: (value?: string): string | undefined => {
        if (!value?.trim()) {
            return 'Legal business name is required';
        }
        return undefined;
    },

    nicknameDba: (value?: string): string | undefined => {
        if (!value?.trim()) {
            return 'Nickname/DBA is required';
        }
        return undefined;
    },

    taxId: (value?: string): string | undefined => {
        if (!value?.trim()) {
            return 'EIN is required';
        }
        return undefined;
    },

    businessType: (value?: string): string | undefined => {
        if (!value) {
            return 'Business type is required';
        }
        return undefined;
    },

    email: (value?: string): string | undefined => {
        if (!value?.trim()) {
            return 'Work email is required';
        }

        const emailRegex = /\S+@\S+\.\S+/;
        if (!emailRegex.test(value)) {
            return 'Please enter a valid email address';
        }

        return undefined;
    },

    addressLineOne: (value?: string): string | undefined => {
        if (!value?.trim()) {
            return 'Address line 1 is required';
        }
        return undefined;
    },

    city: (value?: string): string | undefined => {
        if (!value?.trim()) {
            return 'City is required';
        }
        return undefined;
    },

    state: (value?: string): string | undefined => {
        if (!value?.trim()) {
            return 'State is required';
        }
        return undefined;
    },

    zipcode: (value?: string): string | undefined => {
        if (!value?.trim()) {
            return 'Zipcode is required';
        }
        return undefined;
    },

    country: (value?: string): string | undefined => {
        if (!value?.trim()) {
            return 'Country is required';
        }
        return undefined;
    },

    website: (value?: string): string | undefined => {
        // Website is optional, but if provided must be valid
        if (!value?.trim()) {
            return undefined;
        }

        const urlRegex = /^([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
        if (!urlRegex.test(value)) {
            return 'Please enter a valid website URL';
        }

        return undefined;
    },

    phone: (value?: string, country = 'US'): string | undefined => {
        return validatePhone(value, country);
    },

    parentOrganizationId: (value?: number, isRequired = true): string | undefined => {
        if (isRequired && !value) {
            return 'Parent organization is required';
        }
        return undefined;
    }
};

/**
 * Validates all required fields for an organization
 * @param formData - The organization form data to validate
 * @param country - The country code for phone validation
 * @param requireParentOrganization - Whether parent organization is required (false for parent org editing)
 * @returns Object containing validation errors, or empty object if all valid
 */
export const validateOrganizationForm = (
    formData: SaveOrganization,
    country = 'US',
    requireParentOrganization = true
): Partial<SaveOrganization> => {
    const errors: Partial<SaveOrganization> = {};

    // Validate each field
    const nameError = organizationValidationRules.name(formData.name);
    if (nameError) errors.name = nameError;

    const nicknameDbaError = organizationValidationRules.nicknameDba(formData.nicknameDba);
    if (nicknameDbaError) errors.nicknameDba = nicknameDbaError;

    const taxIdError = organizationValidationRules.taxId(formData.taxId);
    if (taxIdError) errors.taxId = taxIdError;

    const businessTypeError = organizationValidationRules.businessType(formData.businessType);
    if (businessTypeError) errors.businessType = businessTypeError;

    const emailError = organizationValidationRules.email(formData.email);
    if (emailError) errors.email = emailError;

    const phoneError = organizationValidationRules.phone(formData.phone, country);
    if (phoneError) errors.phone = phoneError;

    const addressLineOneError = organizationValidationRules.addressLineOne(formData.addressLineOne);
    if (addressLineOneError) errors.addressLineOne = addressLineOneError;

    const cityError = organizationValidationRules.city(formData.city);
    if (cityError) errors.city = cityError;

    const stateError = organizationValidationRules.state(formData.state);
    if (stateError) errors.state = stateError;

    const zipcodeError = organizationValidationRules.zipcode(formData.zipcode);
    if (zipcodeError) errors.zipcode = zipcodeError;

    const countryError = organizationValidationRules.country(formData.country);
    if (countryError) errors.country = countryError;

    const websiteError = organizationValidationRules.website(formData.website);
    if (websiteError) errors.website = websiteError;

    const parentOrgError = organizationValidationRules.parentOrganizationId(
        formData.parentOrganizationId,
        requireParentOrganization
    );
    if (parentOrgError) {
        // TypeScript workaround: parentOrganizationId error is a string but SaveOrganization expects number
        (errors as Record<string, string>).parentOrganizationId = parentOrgError;
    }

    return errors;
};
